// src/utils/shooterName.ts
//
// Jméno střelce ve veřejných datech (bezpečnost a): rundy živého bodování
// (live_rounds), startovní listina, živé výsledky (live_results), zveřejněné
// výsledky (results_entries.rawName) a tabule jsou čitelné i bez přihlášení.
// Dřívější verze (do 75fae96) ukládaly jako jméno e-mail nebo uid, když
// profil jméno neměl. Taková hodnota se nikdy nezobrazí ani znovu nezapíše
// (safeShooterName); uložená data opraví Příprava dat v Přehledu (krok
// „Jména ve veřejných datech", utils/securityMigration.ts).
//
// Čisté funkce bez Firestore – používá je i roční hodnocení (seasonRanking).

/**
 * Vypadá jako id účtu nebo dokumentu, ne jako jméno: uid Firebase Auth má 28
 * znaků, automatické id dokumentu (importované profily) 20 – písmena bez
 * diakritiky a číslice v jednom slově, uvnitř velké písmeno nebo číslice.
 * Skutečné jméno takhle nevypadá (mezera, diakritika, velké jen první
 * písmeno). Pojistka pro záznamy, u kterých uid střelce neznáme (starší
 * výsledky bez userId).
 */
export function looksLikeAccountId(name: string): boolean {
  return /^[A-Za-z0-9]{20,}$/.test(name) && /[0-9A-Z]/.test(name.slice(1));
}

/**
 * Jméno střelce, které smí do veřejných dat (live_rounds, startovní listina,
 * live_results, výsledky, tabule): oříznuté, bez „@" (e-mail – starší profily
 * bez jména ho měly jako záložní jméno), různé od uid střelce a nevypadá jako
 * id účtu; jinak "".
 */
export function safeShooterName(name: unknown, userId?: string): string {
  const n = typeof name === "string" ? name.trim() : "";
  if (!n || n.indexOf("@") !== -1 || (!!userId && n === userId) || looksLikeAccountId(n)) return "";
  return n;
}

/**
 * Veřejné jméno střelce (tabule, rozhodčí, Živé výsledky, publikace):
 * safeShooterName, jinak „St. č. N" – e-mail ani uid se nikdy neukážou.
 * Víc zdrojů jména se řetězí přes safeShooterName:
 * publicShooterName(safeShooterName(řádek.name, uid) || runda.name, č., uid).
 */
export function publicShooterName(
  name: unknown,
  startNumber: number | string,
  userId?: string
): string {
  return safeShooterName(name, userId) || `St. č. ${startNumber}`;
}

/**
 * Veřejné jméno z profilu users/{uid}: „Jméno Příjmení", jinak displayName,
 * jinak name – vždy jen bezpečné (safeShooterName); "" = profil jméno nemá.
 */
export function profilePublicName(profile: unknown, uid?: string): string {
  if (!profile || typeof profile !== "object") return "";
  const p = profile as { firstName?: unknown; lastName?: unknown; displayName?: unknown; name?: unknown };
  const part = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  return (
    safeShooterName([part(p.firstName), part(p.lastName)].filter(Boolean).join(" "), uid) ||
    safeShooterName(p.displayName, uid) ||
    safeShooterName(p.name, uid)
  );
}

/**
 * Jméno rozhodčího bez jména v profilu (upozornění na převzetí střelce
 * v live_dual_roles čte každý přihlášený – e-mail tam nepatří).
 */
export const JUDGE_NAME_FALLBACK = "Rozhodčí";

/** Záznam zveřejněných výsledků (results_entries) – pole, ze kterých vzniká jméno. */
export interface ResultNameSource {
  rawName?: unknown;
  userId?: unknown;
  startNumber?: unknown;
  sequenceNumber?: unknown;
}

/**
 * Jméno záznamu zveřejněných výsledků k zobrazení (/results, roční hodnocení,
 * sdílení výsledku): uložené jméno, když je bezpečné; jinak „St. č. N"
 * (záznam ze živého bodování nese startovní číslo) nebo „#číslo" (klíč
 * záznamu – členské číslo). Pojistka pro data, která Příprava dat ještě
 * neopravila.
 */
export function resultEntryName(entry: ResultNameSource): string {
  const userId = typeof entry.userId === "string" && entry.userId ? entry.userId : undefined;
  const name = safeShooterName(entry.rawName, userId);
  if (name) return name;
  if (typeof entry.startNumber === "number" && Number.isFinite(entry.startNumber)) {
    return `St. č. ${entry.startNumber}`;
  }
  const key =
    typeof entry.sequenceNumber === "string" || typeof entry.sequenceNumber === "number"
      ? String(entry.sequenceNumber).trim()
      : "";
  return key ? `#${key}` : "";
}
